import React, { useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Dialog } from 'primereact/dialog';
import 'primeicons/primeicons.css';
import ContactTable from '../../components/ContactTable/ContactTable';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Tooltip } from 'primereact/tooltip';


/**
 * VariantsTable – shows variant list with per‑column and global filtering.
 */
const VariantsTable = ({
    VariantData,
    setSelectedVariants,
    setUniprotAccessionNumber,
    setPositionSummary
}) => {
    /* ========================================
     * Refs & State
     * ====================================== */
    const dt = useRef(null);
    const [expandedRows, setExpandedRows] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedContactData, setSelectedContactData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    /* ========================================
     * Columns definitions
     * ====================================== */
    const columns = [
        { field: 'anPosition', header: 'Position' },
        { field: 'locationG37', header: 'Location hg19' },
        { field: 'locationG38', header: 'Location hg38' },
        { field: 'ref', header: 'REF' },
        { field: 'alt', header: 'ALT' },
        { field: 'variant', header: 'Variant ID' },
        { field: 'consequence', header: 'Consequence' },
        { field: 'clinicalSignificance', header: 'Clinical significance' },
        { field: 'disease', header: 'Disease' },
        { field: 'proteinPosition', header: 'Protein position' },
        { field: 'aaChange', header: 'AA change' },
        { field: 'codon', header: 'Codon' },
        { field: 'alphamissenseScore', header: 'AlphaMissense score' },
        { field: 'alphamissenseClass', header: 'AlphaMissense class' },
    ];

    // Columns shown by user choice (MultiSelect)
    const multiSelectOptions = columns.map((col) => ({ label: col.header, value: col.field }));
    const [visibleColumns, setVisibleColumns] = useState(columns);
    const numericFields = ['proteinPosition', 'alphamissenseScore'];

    /* ========================================
     * Per‑column filter helpers
     * ====================================== */
    const renderNumericFilter = (options) => (
        <InputText
            type="number"
            value={options.value ?? ''}
            onChange={(e) => options.filterCallback(e.target.value, options.index)}
            placeholder="Enter number"
            className="p-column-filter"
        />
    );

    // Build initial filters object programmatically so we keep DRY
    const buildInitialFilters = () => {
        const f = {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        };
        columns.forEach((col) => {
            f[col.field] = {
                operator: FilterOperator.AND,
                constraints: [
                    {
                        value: null,
                        matchMode: numericFields.includes(col.field)
                            ? FilterMatchMode.EQUALS
                            : FilterMatchMode.CONTAINS,
                    },
                ],
            };
        });
        return f;
    };

    const [filters, setFilters] = useState(buildInitialFilters());
    const clearFilters = () => setFilters(buildInitialFilters());

    // Global search helpers
    const globalValue = filters['global']?.value || '';
    const onGlobalFilterChange = (e) => {
        const val = e.target.value.replace(/\s+/g, "");
        setFilters((prev) => ({
            ...prev,
            global: { ...prev.global, value: val },
        }));
    };

    /* ========================================
     * Derived columns with filter UI enabled
     * ====================================== */
    const dynamicColumns = visibleColumns.map((col) => {
        const isNumeric = numericFields.includes(col.field);
        return (
            <Column
                className="nobreak"
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                filter
                filterDisplay="menu"
                dataType={isNumeric ? 'numeric' : 'text'}
                filterElement={isNumeric ? renderNumericFilter : undefined}
                filterPlaceholder={`Search by ${col.header}`}
                body={col.body}
            />
        );
    });

    /* ========================================
     * Expanded rows (details) template
     * ====================================== */
    const generateContactSummary = (contacts, position) => {
        if (!Array.isArray(contacts) || contacts.length === 0) return 'no_contact';
        contacts.forEach((c) => {
            if (c && c.pdbId) c.anPosition = position; // annotate
        });
        return (
            <Button
                label="Show"
                icon="pi pi-external-link"
                onClick={() => {
                    setSelectedContactData(contacts);
                    setDialogVisible(true);
                }}
            />
        );
    };


    /* =======================================
     * Row selection handlers
     * ====================================== */
    const handleRowSelect = (e) => {
        const rows = e.value;
        setSelectedRows(rows);
        setPositionSummary('');

        const selectedPositions = rows.map((r) => r.proteinPosition);
        let accession = rows.map((r) => r.anPosition);
        if (accession.length) {
            accession = accession[0].split('_')[0];
            setUniprotAccessionNumber(accession);
        }
        setSelectedVariants(selectedPositions);
    };

    const onRowUnselect = (e) => {
        const updated = selectedRows.filter((r) => r.proteinPosition !== e.data.proteinPosition);
        setSelectedRows(updated);
        setSelectedVariants(updated.map((r) => r.proteinPosition));
    };

    const handleResetData = () => {
        setSelectedRows([]);
        setSelectedVariants([]);
        setPositionSummary('');
        clearFilters();
    };

    /* ========================================
     * Column toggle (MultiSelect)
     * ====================================== */
    const onColumnToggle = (e) => {
        const fields = e.value;
        setVisibleColumns(columns.filter((c) => fields.includes(c.field)));
    };

    /* ========================================
     * Export helpers
     * ====================================== */
    const exportCSV = (selectionOnly = false) => dt.current.exportCSV({ selectionOnly });

    const exportExcel = async (dataToExport) => {
        const xlsx = await import('xlsx');
        const worksheet = xlsx.utils.json_to_sheet(dataToExport);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        import('file-saver').then((module) => {
            if (module && module.default) {
                const blob = new Blob([excelBuffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
                });
                module.default.saveAs(blob, `variants_${Date.now()}.xlsx`);
            }
        });
    };

    /* ========================================
     * URL body template (ClinVar column)
     * ====================================== */
    const urlBodyTemplate = (row) => (
        <Button
            icon="pi pi-external-link"
            className="p-button-rounded"
            onClick={() => row.url !== '#' && window.open(row.url, '_blank')}
            disabled={row.url === '#'}
        />
    );

     const tagsBodyTemplate = (rowData) => {
     if (!rowData.summary || !rowData.anPosition) return null;
    const annotations = rowData.summary;
    if (!annotations) return null;

    return (
      <div className="flex gap-1 justify-content-center">
        {annotations.is_functional && (
         <>
           <Tooltip target=".is_functional" />
           <i className="is_functional pi pi-cog p-1" style={{ color: '#708090' }} data-pr-tooltip="Functional Significance"
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {annotations.has_intra_contact && (
         <>
          <Tooltip target=".has_intra_contact" />
          <i className="has_intra_contact pi pi-globe p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {annotations.has_af_intra_contact && (
         <>
          <Tooltip target=".has_af_intra_contact" />
          <i className="has_af_intra_contact pi pi pi-star p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Intra-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
          {annotations.has_inter_contact && (
          <>
          <Tooltip target=".has_inter_contact" />
          <i className="has_inter_contact pi pi-sitemap p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Contact"
           data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
          </>
        )}
        {annotations.has_af_inter_contact && (
         <>
          <Tooltip target=".has_af_inter_contact" />
          <i className="has_af_inter_contact pi pi-star-fill p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Inter-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        { annotations.has_ligand  && (
          <>
          <Tooltip target=".has_ligand" />
          <i className="has_ligand pi pi-ethereum p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Ligand Interaction"
           data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
          </>
        )}
      </div>
    );
  };

    /* ========================================
     * Table header – global search, buttons
     * ====================================== */
    const header = (
        <div className="row align-items-center">
            <div className="col mb-2 d-flex align-items-center">
                <IconField iconPosition="left" className="me-1">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        type="search"
                        value={globalValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Global Search"
                    />
                </IconField>
                <Button
                    type="button"
                    icon="pi pi-filter-slash"
                    label="Clear"
                    outlined
                    onClick={clearFilters}
                    className="me-2"
                />
                <Button type="button" icon="pi pi-replay" text onClick={handleResetData} tooltip="Unselect rows" />
            </div>
            <div className="col d-flex justify-content-end align-items-center">
                <MultiSelect
                    value={visibleColumns.map((c) => c.field)}
                    options={multiSelectOptions}
                    onChange={onColumnToggle}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select Columns"
                    display="chip"
                    style={{ width: '200px', marginRight: '10px' }}
                />
                <Button icon="pi pi-file" className="p-button-rounded" onClick={() => exportCSV(false)} />
                <Button
                    icon="pi pi-file-excel"
                    className="p-button-success p-button-rounded"
                    onClick={() => exportExcel(VariantData)}
                />
            </div>
        </div>
    );

    /* ========================================
     * Main render
     * ====================================== */
    return (
        <>
            <DataTable
                ref={dt}
                value={VariantData}
                paginator
                rows={5}
                removableSort
                header={header}
                filters={filters}
                onFilter={(e) => setFilters(e.filters)}
                globalFilterFields={columns.map((c) => c.field)}
                selectionMode="multiple"
                selection={selectedRows}
                onSelectionChange={handleRowSelect}
                onRowUnselect={onRowUnselect}
                className="nobreak"
                tableStyle={{ minWidth: '60rem' }}
                responsiveLayout="scroll"
            >
             <Column
                  header="Tags"
                  body={tagsBodyTemplate}
                  style={{ width: '5rem' }}
                  align="center"
              />
                {dynamicColumns}
                <Column field="url" header="ClinVar URL" body={urlBodyTemplate} />
            </DataTable>

            <Dialog
                header="Contact Details"
                visible={dialogVisible}
                style={{ width: '70vw' }}
                onHide={() => setDialogVisible(false)}
            >
                <ContactTable contactData={selectedContactData} />
            </Dialog>
        </>
    );
};

export default VariantsTable;
